import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, toRef, computed, toRefs, onMounted, onUnmounted } from 'vue';
  import { apiGetDetailedAddress } from '@/api/tenants';
  import { Floor as ApiFloor } from '@/types/api/tenants';
  import { Floor } from '@/types/widgets/tenantsWidget/tenantsWidget';
  import Loader from '@/components/common/Loader.vue';
  import { divideArray } from '@/helpers';
  import { getRBGAColor } from '@/helpers/utils';

  
  import { defineComponent } from 'vue';
  import { ALIGN_MODES } from '@/models/layoutDesigner';
  import { useStore } from '@/store';

  const __default__ = defineComponent({
    name: 'TenantsViewer',
  });

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    widgetData: { type: Object, default: {} },
  },
  setup(__props) {

const props = __props;

  

  const timer = ref<ReturnType<typeof setInterval> | null>(null);
  const currentPage = ref<number>(1);
  const isLoading = ref(false);
  const errorLoading = ref(false);
  const fetchedFloors = ref<ApiFloor[]>([]);
  const initialLoad = ref<boolean>(true);
  const skipInitialAnimation = ref<boolean>(true);

  const { tenants } = toRefs(props.widgetData);
  const slideInterval = toRef(props.widgetData.tenants, 'interval');
  const allFloorsVisible = toRef(props.widgetData.tenants, 'allFloorsVisible');

  watch([slideInterval, allFloorsVisible], () => {
    resetSlide();
  });

  const containerStyle = computed(() => {
    return {
      display: tenants.value.verticalMode ? 'flex' : 'grid',
      flexDirection: 'column',
      gridAutoFlow: 'row',
      gridTemplate: 'repeat(16, auto) / 1fr 1fr',
      paddingTop: `${tenants.value.topPadding || 20}px`,
    };
  });

  const wrapperStyle = computed(() => {
    return {
      backgroundColor: getRBGAColor(
        tenants.value.backgroundColor,
        Number(tenants.value.opacity) / 100,
      ),
    };
  });

  const apartmentsContainerStyle = computed(() => {
    const justifyContent = !tenants.value.groupNamesAndNumbers
      ? 'space-between'
      : tenants.value.textAlignment;

    return {
      flexDirection: !tenants.value.numbersOnTheLeft ? 'row' : 'row-reverse',
      justifyContent,
      paddingTop:
        !!tenants.value.verticalPadding || tenants.value.verticalPadding === 0
          ? `${tenants.value.verticalPadding}px`
          : '8px',
      paddingBottom:
        !!tenants.value.verticalPadding || tenants.value.verticalPadding === 0
          ? `${tenants.value.verticalPadding}px`
          : '8px',
    };
  });

  const titleStyle = computed(() => {
    return {
      fontFamily: tenants.value.titleFont,
      fontSize: `${tenants.value.titleFontSize}px`,
      lineHeight: `${tenants.value.titleFontSize * 1.3}px`,
      color: tenants.value.titleFontColor,
      backgroundColor: tenants.value.titleBackgroundColor,
      textAlign: tenants.value.titleAlignment || ALIGN_MODES.LEFT_ALIGN,
    };
  });

  const addressTitleStyle = computed(() => {
    return {
      fontFamily: tenants.value.addressFont || 'Arial',
      fontSize: `${tenants.value.addressFontSize || 24}px`,
      lineHeight: `${(tenants.value.addressFontSize || 24) * 1.3}px`,
      color: tenants.value.addressFontColor || '#000000',
      backgroundColor: tenants.value.addressBackgroundColor || 'transparent',
      textAlign: tenants.value.addressAlignment || ALIGN_MODES.LEFT_ALIGN,
      paddingTop: `${tenants.value.addressVerticalPadding === undefined || tenants.value.addressVerticalPadding === 0 ? 10 : tenants.value.addressVerticalPadding}px`,
      paddingBottom: `${tenants.value.addressVerticalPadding === undefined || tenants.value.addressVerticalPadding === 0 ? 10 : tenants.value.addressVerticalPadding}px`,
      paddingLeft: `${tenants.value.addressHorizontalPadding === undefined || tenants.value.addressHorizontalPadding === 0 ? 20 : tenants.value.addressHorizontalPadding}px`,
      paddingRight: `${tenants.value.addressHorizontalPadding === undefined || tenants.value.addressHorizontalPadding === 0 ? 20 : tenants.value.addressHorizontalPadding}px`,
      display: tenants.value.showAddress ? 'block' : 'none',
    };
  });

  const tenantNameStyle = computed(() => {
    return {
      fontFamily: tenants.value.floorFont,
      fontSize: `${tenants.value.floorFontSize}px`,
      lineHeight: `${tenants.value.floorFontSize * 1.3}px`,
      color: tenants.value.floorFontColor,
      textAlign: tenants.value.textAlignment || ALIGN_MODES.LEFT_ALIGN,
      width: !tenants.value.groupNamesAndNumbers ? '100%' : 'auto',
    };
  });

  const tenantNumberStyle = computed(() => {
    return {
      fontFamily: tenants.value.roomsFont,
      fontSize: `${tenants.value.numberFontSize}px`,
      lineHeight: `${tenants.value.numberFontSize * 1.3}px`,
      color: tenants.value.numberFontColor,
      textAlign: tenants.value.numberAlignment || ALIGN_MODES.LEFT_ALIGN,
      display: !tenants.value.hideNumbers ? 'block' : 'none',
    };
  });

  const paginationStyle = computed(() => ({
    fontFamily: tenants.value.paginationFont,
    fontSize: `${tenants.value.paginationFontSize}px`,
    lineHeight: `${tenants.value.paginationFontSize * 1.2}px`,
    color: tenants.value.paginationFontColor,
    justifyContent: tenants.value.paginationAlignment || ALIGN_MODES.CENTER_ALIGN,
    height: tenants.value.paginationInText ? `${tenants.value.paginationFontSize + 16}px` : 'auto',
  }));

  const pages = computed<Floor[][]>(() => {
    if (!tenants.value.floors) return [];

    const filteredFloors = tenants.value.allFloorsVisible
      ? tenants.value.floors
      : tenants.value.floors.filter((floor: Floor) => floor?.visible !== false);

    return divideArray(filteredFloors, tenants.value.floorsOnSlide);
  });

  function setCurrentPage(value: number) {
    currentPage.value = value;
  }

  function nextTenantsSlide() {
    currentPage.value = currentPage.value < pages.value.length ? currentPage.value + 1 : 1;
  }

  function destroyTimer() {
    if (!timer.value) return;

    clearInterval(timer.value);
  }

  function resetSlide() {
    destroyTimer();
    setCurrentPage(1);

    if (pages.value.length <= 1) {
      return;
    }

    if (initialLoad.value) {
      initialLoad.value = false;
      setTimeout(() => {
        skipInitialAnimation.value = false;

        nextTenantsSlide();

        timer.value = setInterval(nextTenantsSlide, slideInterval.value * 1000);
      }, slideInterval.value * 1000);
    } else {
      skipInitialAnimation.value = false;
      timer.value = setInterval(nextTenantsSlide, slideInterval.value * 1000);
    }
  }

  async function fetchTenants() {
    const store = useStore();
    if (tenants.value.address.id === 'demo' || store.state.templateDesigner.isSavingTemplate)
      return;
    errorLoading.value = false;
    isLoading.value = true;

    try {
      const response = await apiGetDetailedAddress(tenants.value.address?.id);
      const { floors } = response;

      tenants.value.floors = tenants.value.floors.map((floor: Floor) => {
        const currentFloor = floors.find(
          (currentFloor: ApiFloor) => currentFloor.floor === floor.floor,
        );

        const displayFloorName = floor?.displayFloorName
          ? floor.displayFloorName
          : currentFloor?.displayFloorName;

        return {
          ...floor,
          apartments: currentFloor?.apartments,
          visible: floor?.visible === false ? false : true,
          displayFloorName:
            displayFloorName === null && currentFloor?.floor === 0
              ? 'Bottenvåning'
              : displayFloorName,
        };
      });

      fetchedFloors.value = floors;
    } catch (error) {
      console.log('FetchTenants - error:', error);
      errorLoading.value = true;
    }

    isLoading.value = false;
  }

  const prepareName = (names: string[]) => {
    if (!tenants.value.shortenNames) return names.join(', ');

    return names
      .map((name) => {
        const words = name.split(' ');

        if (words.length < 2) return name;

        const firstNameInitial = words[0][0] + '.';
        const lastName = words[words.length - 1];

        return `${firstNameInitial} ${lastName}`;
      })
      .join(', ');
  };

  onMounted(() => {
    resetSlide();
    fetchTenants();
  });

  onUnmounted(() => {
    destroyTimer();
  });

return { __sfc: true,props, timer, currentPage, isLoading, errorLoading, fetchedFloors, initialLoad, skipInitialAnimation, tenants, slideInterval, allFloorsVisible, containerStyle, wrapperStyle, apartmentsContainerStyle, titleStyle, addressTitleStyle, tenantNameStyle, tenantNumberStyle, paginationStyle, pages, setCurrentPage, nextTenantsSlide, destroyTimer, resetSlide, fetchTenants, prepareName, Loader }
}

})